'use client';

import React, { useState, useEffect } from 'react';
import Icon from '@/components/ui/AppIcon';

interface PremiumAccessModalProps {
  isOpen: boolean;
  onClose: () => void;
  resourceTitle: string;
}

const PremiumAccessModal = ({ isOpen, onClose, resourceTitle }: PremiumAccessModalProps) => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    company: '',
    role: '',
  });
  const [isSubmitted, setIsSubmitted] = useState(false);

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitted(true);
    setTimeout(() => {
      onClose();
      setIsSubmitted(false);
      setFormData({ name: '', email: '', company: '', role: '' });
    }, 2000);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
      <div className="bg-background rounded-lg shadow-modal max-w-md w-full max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-background border-b border-border p-6 flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 bg-accent/10 rounded-full flex items-center justify-center">
              <Icon name="StarIcon" size={20} variant="solid" className="text-accent" />
            </div>
            <div>
              <h2 className="text-xl font-bold font-headline text-foreground">Premium Access</h2>
              <p className="text-sm text-muted-foreground">Unlock exclusive content</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 rounded-md text-muted-foreground hover:text-foreground hover:bg-muted transition-all duration-300"
            aria-label="Close modal"
          >
            <Icon name="XMarkIcon" size={24} variant="outline" />
          </button>
        </div>

        <div className="p-6">
          {isSubmitted ? (
            <div className="text-center py-8">
              <div className="w-16 h-16 bg-success/10 rounded-full flex items-center justify-center mx-auto mb-4">
                <Icon name="CheckCircleIcon" size={32} variant="solid" className="text-success" />
              </div>
              <h3 className="text-xl font-bold font-headline text-foreground mb-2">Access Granted!</h3>
              <p className="text-muted-foreground">
                Check your email for the download link and additional resources.
              </p>
            </div>
          ) : (
            <>
              <div className="bg-muted/50 rounded-lg p-4 mb-6">
                <p className="text-sm font-medium text-foreground mb-1">Requesting access to:</p>
                <p className="text-sm text-muted-foreground line-clamp-2">{resourceTitle}</p>
              </div>

              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label htmlFor="name" className="block text-sm font-medium text-foreground mb-2">
                    Full Name *
                  </label>
                  <input
                    id="name"
                    type="text"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    required
                    className="w-full px-4 py-2.5 border border-input rounded-md focus:outline-none focus:ring-2 focus:ring-ring text-sm"
                    placeholder="John Smith"
                  />
                </div>

                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-foreground mb-2">
                    Work Email *
                  </label>
                  <input
                    id="email"
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    required
                    className="w-full px-4 py-2.5 border border-input rounded-md focus:outline-none focus:ring-2 focus:ring-ring text-sm"
                    placeholder="john.smith@company.com"
                  />
                </div>

                <div>
                  <label htmlFor="company" className="block text-sm font-medium text-foreground mb-2">
                    Company *
                  </label>
                  <input
                    id="company"
                    type="text"
                    value={formData.company}
                    onChange={(e) => setFormData({ ...formData, company: e.target.value })}
                    required
                    className="w-full px-4 py-2.5 border border-input rounded-md focus:outline-none focus:ring-2 focus:ring-ring text-sm"
                    placeholder="Acme Corporation"
                  />
                </div>

                <div>
                  <label htmlFor="role" className="block text-sm font-medium text-foreground mb-2">
                    Job Role *
                  </label>
                  <select
                    id="role"
                    value={formData.role}
                    onChange={(e) => setFormData({ ...formData, role: e.target.value })}
                    required
                    className="w-full px-4 py-2.5 border border-input rounded-md focus:outline-none focus:ring-2 focus:ring-ring text-sm appearance-none bg-background"
                  >
                    <option value="">Select your role</option>
                    <option value="c-suite">C-Suite Executive</option>
                    <option value="vp-director">VP / Director</option>
                    <option value="manager">Manager</option>
                    <option value="consultant">Consultant</option>
                    <option value="other">Other</option>
                  </select>
                </div>

                <div className="pt-4">
                  <button
                    type="submit"
                    className="w-full px-6 py-3 bg-secondary text-secondary-foreground rounded-md font-semibold font-cta hover:bg-secondary/90 transition-all duration-300 flex items-center justify-center space-x-2"
                  >
                    <Icon name="LockOpenIcon" size={20} variant="outline" />
                    <span>Get Instant Access</span>
                  </button>
                </div>

                <p className="text-xs text-muted-foreground text-center">
                  By submitting, you agree to receive communications from AI Change Leaders. We respect your privacy.
                </p>
              </form>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default PremiumAccessModal;